import { Check, Loader2, AlertCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import BuyerForm from "./buyer-form";
import DealSummary from "./deal-summary";
import DealStagePopup from "../DealStagePopup";
import {
  getAllContactsFromFirebase,
  getContactsByIds,
  getContactTypes,
  getElectronicLockboxes,
  getPushButtonLockbox,
  updateDealById,
  updateElectronicLockboxById,
  updatePushButtonLockboxById,
} from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";
import SellerForm from "./seller-form";

function ListingDetail({ deal, onSubmit, updatedealstage }) {
  const [{ user }] = useStateValue();
  const [dealstagepopup, setdealstagepopup] = useState(false);
  // Side is fixed by how the deal was created (Sale Turn In / Listing Request
  // → seller, Buyer Request → buyer). No user toggle here.
  const activeTab = deal?.dealtype === "Buyer" ? "buyer" : "seller";
  // Auto-save model: the listing-details form is always editable. Changes
  // debounce-save automatically (no Save button). Status badge in the header
  // shows "Saving…" / "Saved" / "Save failed". Lockbox reassignment still
  // flows through the existing handleSave path so its side-effects fire too.
  const isEdit = true;
  const [isLoad, setIsLoad] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const baselineRef = useRef(null);
  const saveTimerRef = useRef(null);
  const [electronicLockboxes, setElectronicLockboxes] = useState([]);
  const [pushButtonLockboxes, setPushButtonLockboxes] = useState([]);
  const [contactTypeData, setContactTypeData] = useState([]);
  const [contactSyncData, setContactSyncData] = useState([]);
  const [participants, setParticipants] = useState([]);

  const [selectedContacts, setSelectedContacts] = useState({
    contactSyncId: [],
    contactType: [],
  });

  const defaultSellerFormData = {
    listingAppointment: "",
    targetListDate: "",
    suggestedPrice: "",
    listPrice: "",
    fileDeadline: "",
    occupied: "",
    lockboxType: "",
    selectedLockbox: null,
    isLinkCreated: false,

    staging: "",
    stagingProvider: "",
    stagingProviderEmail: "",
    stagingBid: "",
    stagingInstall: "",
    destaging: "",
    photographerDate: "",
    photographer: "",
    photographerEmail: "",
    // Per-inspection vendor info, keyed by inspection key. Same vendor can
    // be assigned to multiple inspections — the UI quick-picks them.
    //   { fullInspection: { name, email }, radon: { name, email }, ... }
    inspectionVendors: {},

    preInspections: {
      fullInspection: false,
      radon: false,
      sewerScope: false,
      cityInspection: false,
      mold: false,
      leadPaint: false,
      pest: false,
      chimney: false,
    },

    finalAcceptanceDate: "",
    preSignDate: "",
    recissionPeriod: "",
    rescissionDeadline: "",
    buyerInspection: "",
    appraisal: "",
    appraisalComplete: "",
    finalWalkthrough: "",
    // Same milestones-section additions as the buyer form — kept in
    // sync so the listing side looks and behaves identically.
    inspection: "",
    inspectionDeadline: "",
    financingType: "",
    financingDeadline: "",
    offerPrice: "",
    earnestMoney: "",
    earnestMoneyReceivedDate: "",
    closingdate: "",
  };

  const defaultBuyerFormData = {
    finalAcceptanceDate: "",
    // preSignDate omitted — pre-sign is a seller-side step
    recissionPeriod: "",
    rescissionDeadline: "",
    buyerInspection: "",
    appraisal: "",
    appraisalComplete: "",
    finalWalkthrough: "",
    // Carried in from turn-in but editable here so the team can fix them
    // post-acceptance if needed.
    inspection: "",
    inspectionDeadline: "",
    financingType: "",
    financingDeadline: "",
    // Financials editable from the milestones section so the team can
    // adjust if the contract changes after turn-in.
    offerPrice: "",
    earnestMoney: "",
    // New: date the earnest money was actually received by the brokerage.
    // Tracked separately from earnestMoney (which is the agreed amount).
    earnestMoneyReceivedDate: "",
    // Closing date is also editable from the milestones section so the
    // team can adjust if the contract date shifts.
    closingdate: "",
  };

  const [buyerFormData, setBuyerFormData] = useState(defaultBuyerFormData);
  const [sellerFormData, setSellerFormData] = useState(defaultSellerFormData);

  useEffect(() => {
    loadLockboxes();
    loadContactTypes();
    loadSyncContacts();
  }, []);

  const loadLockboxes = async () => {
    try {
      const electronicData = await getElectronicLockboxes();
      const formattedElectronicData = electronicData.map((item) => ({
        ...item,
        displayLabel: `${item.lockboxNumber} . ${item.status} . Battery ${item.battery}%`,
      }));

      const pushButtonData = await getPushButtonLockbox();
      const formattedPushButtonData = pushButtonData.map((item) => ({
        ...item,
        displayLabel: `${item.lockboxNumber} . Code ${item.code} . ${item.status}`,
      }));

      setElectronicLockboxes(formattedElectronicData);
      setPushButtonLockboxes(formattedPushButtonData);
    } catch (error) {
      console.error("Error loading lockboxes:", error);
    }
  };

  const loadContactTypes = async () => {
    const data = await getContactTypes();
    setContactTypeData(data);
  };

  const loadSyncContacts = async () => {
    try {
      const userContacts = await getAllContactsFromFirebase(user.id);

      const dealContactIds =
        deal?.contactType?.map((item) => item.contactId).filter(Boolean) || [];

      const missingIds = dealContactIds.filter(
        (id) =>
          !userContacts.some((contact) => String(contact.id) === String(id)),
      );

      let extraContacts = [];

      if (missingIds.length) {
        extraContacts = await getContactsByIds(missingIds);
      }

      const mergedContacts = [...userContacts];

      extraContacts.forEach((contact) => {
        if (!mergedContacts.some((c) => String(c.id) === String(contact.id))) {
          mergedContacts.push(contact);
        }
      });

      setContactSyncData(mergedContacts);
    } catch (err) {
      console.error(err);
    }
  };

  const handleBuyerFormChange = (field, value) => {
    setBuyerFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSellerFormChange = (field, value) => {
    setSellerFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setIsLoad(true);

    try {
      const contactSyncId = participants
        .map((p) => p.contactId)
        .filter(Boolean);

      const contactType = participants.map((p) => ({
        typeId: p.typeId || p.id,
        type: p.type,
        contactId: p.contactId || "",
      }));

      const baseData = activeTab === "seller" ? sellerFormData : buyerFormData;

      const oldLockboxId =
        typeof deal.selectedLockbox === "object"
          ? deal.selectedLockbox?.id
          : deal.selectedLockbox || "";

      const oldLockboxType = deal.lockboxType || "";

      const newLockboxId =
        typeof sellerFormData.selectedLockbox === "object"
          ? sellerFormData.selectedLockbox?.id
          : sellerFormData.selectedLockbox || "";

      const newLockboxType = sellerFormData.lockboxType || "";

      const oldLockbox =
        oldLockboxType === "Push Button"
          ? pushButtonLockboxes.find((item) => item.id === oldLockboxId)
          : electronicLockboxes.find((item) => item.id === oldLockboxId);

      const selectedLockbox =
        newLockboxType === "Push Button"
          ? pushButtonLockboxes.find((item) => item.id === newLockboxId)
          : electronicLockboxes.find((item) => item.id === newLockboxId);

      if (
        oldLockboxId &&
        oldLockbox &&
        (oldLockboxId !== newLockboxId || oldLockboxType !== newLockboxType)
      ) {
        const oldUpdateFn =
          oldLockboxType === "Push Button"
            ? updatePushButtonLockboxById
            : updateElectronicLockboxById;

        await oldUpdateFn(oldLockboxId, {
          ...oldLockbox,
          status: "Available",
          assignedDealId: "",
          assignedDealLabel: "",
        });
      }

      const updatedDealPayload = {
        ...deal,
        ...baseData,
        selectedLockbox:
          activeTab === "seller"
            ? newLockboxId || ""
            : deal.selectedLockbox || "",

        lockboxType:
          activeTab === "seller"
            ? newLockboxType || ""
            : deal.lockboxType || "",
        contactSyncId,
        contactType,
      };

      await updateDealById(String(deal.createdat), updatedDealPayload);

      if (newLockboxId && selectedLockbox) {
        const updateFn =
          newLockboxType === "Push Button"
            ? updatePushButtonLockboxById
            : updateElectronicLockboxById;

        await updateFn(newLockboxId, {
          ...selectedLockbox,
          status: "Assigned",
          assignedDealId: deal.createdat,
          assignedDealLabel:
            deal.propertyaddress || deal.propertyName || deal.address || "N/A",
        });
      }

      await loadLockboxes();

      // Auto-save model: local sellerFormData is the source of truth.
      // Don't call onSubmit() here — it triggers fetchDeal in the parent,
      // which then resets this form's state from deal data, racing with
      // any in-flight clicks (the bug where pill clicks "didn't stick").
      // No success toast either — the auto-save indicator handles it.
    } catch (error) {
      console.error("Save Error:", error);
      // Rethrow so handleAutoSave can flip status to "error".
      throw error;
    } finally {
      setIsLoad(false);
    }
  };

  // === AUTO-SAVE ===
  // After deal + form data settle, snapshot the baseline. Future diffs against
  // this snapshot determine whether to fire an auto-save.
  useEffect(() => {
    if (!deal) return;
    // Wait one tick for setSellerFormData / setBuyerFormData / setParticipants
    // to apply, then snapshot.
    const t = setTimeout(() => {
      baselineRef.current = JSON.stringify({
        s: sellerFormData,
        b: buyerFormData,
        p: participants,
      });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.createdat, contactTypeData?.length, activeTab]);

  // Watch for form changes and schedule a debounced save.
  useEffect(() => {
    if (!baselineRef.current) return;
    const current = JSON.stringify({
      s: sellerFormData,
      b: buyerFormData,
      p: participants,
    });
    if (current === baselineRef.current) return;

    // Visual cue: the user has unsaved changes pending.
    setAutoSaveStatus((s) => (s === "saving" ? s : "idle"));

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus("saving");
      try {
        await handleSave();
        baselineRef.current = current;
        setAutoSaveStatus("saved");
        setLastSavedAt(Date.now());
      } catch {
        setAutoSaveStatus("error");
      }
    }, 1500);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerFormData, buyerFormData, participants]);

  // Helpful: save on tab close / route change if there are pending changes.
  useEffect(() => {
    const handler = (e) => {
      const current = JSON.stringify({
        s: sellerFormData,
        b: buyerFormData,
        p: participants,
      });
      if (baselineRef.current && current !== baselineRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [sellerFormData, buyerFormData, participants]);

  const handleContactSelection = ({
    typeId,
    type,
    contactId,
    remove = false,
  }) => {
    setSelectedContacts((prev) => {
      let updatedSyncIds = [...prev.contactSyncId];
      let updatedTypes = [...prev.contactType];

      if (remove) {
        const removedContact = prev.contactType.find(
          (t) => String(t.typeId) === String(typeId),
        );

        if (removedContact?.contactId) {
          updatedSyncIds = updatedSyncIds.filter(
            (id) => String(id) !== String(removedContact.contactId),
          );
        }

        updatedTypes = updatedTypes.filter(
          (t) => String(t.typeId) !== String(typeId),
        );

        return {
          contactSyncId: updatedSyncIds,
          contactType: updatedTypes,
        };
      }

      if (contactId) {
        if (!updatedSyncIds.includes(contactId)) {
          updatedSyncIds.push(contactId);
        }
      }

      updatedSyncIds = updatedSyncIds.filter(Boolean);

      const index = updatedTypes.findIndex(
        (t) => String(t.typeId) === String(typeId),
      );

      if (index > -1) {
        updatedTypes[index] = {
          ...updatedTypes[index],
          type,
          contactId,
        };
      } else {
        updatedTypes.push({
          typeId,
          type,
          contactId,
        });
      }

      return {
        contactSyncId: updatedSyncIds,
        contactType: updatedTypes,
      };
    });
  };

  const resetFormData = () => {
    if (!deal) return;

    const listingData = deal || {};

    setBuyerFormData({
      ...defaultBuyerFormData,
      ...listingData,
      preInspections: {
        ...defaultBuyerFormData.preInspections,
        ...(listingData.preInspections || {}),
      },
    });

    setSellerFormData({
      ...defaultSellerFormData,
      ...listingData,
      preInspections: {
        ...defaultSellerFormData.preInspections,
        ...(listingData.preInspections || {}),
      },
    });

    setSelectedContacts({
      contactSyncId: listingData.contactSyncId || [],
      contactType: listingData.contactType || [],
    });

    // 🔥 IMPORTANT FIX
    setParticipants(
      buildParticipants(listingData, contactTypeData, contactSyncData),
    );
  };

  // SIRF buildParticipants FUNCTION MODIFY KARO
// baki sara flow same rahega

const buildParticipants = (deal, contactTypeData, contactSyncData) => {
  const savedTypes = deal?.contactType || [];

  // ✅ AGAR DEAL ME DATA MOJOD HAI TO PURANA FLOW
  if (savedTypes.length > 0) {
    return savedTypes.map((item) => {
      const matchedType = contactTypeData.find(
        (ct) => String(ct.id) === String(item.typeId),
      );

      const contact = contactSyncData?.find(
        (c) => String(c.id) === String(item.contactId),
      );

      return {
        id: item.typeId,
        typeId: item.typeId,
        type: item.type || matchedType?.type || "",
        contactId: item.contactId || "",
        fullName: contact?.name || "",
        phone: contact?.phone || "",
        email: contact?.email || "",
        isCustom: false,
        search: {
          fullName: contact?.name || "",
          phone: contact?.phone || "",
          email: contact?.email || "",
        },
        dropdown: null,
        filtered: [],
      };
    });
  }

  // ✅ AGAR DEAL ME CONTACT TYPE DATA NA HO
  // TAB clientType FILTER LAGAO
  return (contactTypeData || [])
    .filter((type) => {
      const clientType = (type.clientType || "").toLowerCase();

      if (activeTab === "seller") {
        return clientType === "seller" || clientType === "both";
      }

      if (activeTab === "buyer") {
        return clientType === "buyer" || clientType === "both";
      }

      return true;
    })
    .map((type) => ({
      id: type.id,
      typeId: type.id,
      type: type.type,
      contactId: "",
      fullName: "",
      phone: "",
      email: "",
      isCustom: false,
      search: {
        fullName: "",
        phone: "",
        email: "",
      },
      dropdown: null,
      filtered: [],
    }));
};

  const handledata = () => {
    loadLockboxes();
    onSubmit();
  };

  useEffect(() => {
    if (!deal) return;
    if (!contactTypeData) return;

    const listingData = deal || {};

    setBuyerFormData({
      ...defaultBuyerFormData,
      ...listingData,
    });

    setSellerFormData({
      ...defaultSellerFormData,
      ...listingData,
    });

    setSelectedContacts({
      contactSyncId: listingData.contactSyncId || [],
      contactType: listingData.contactType || [],
    });

    setParticipants(
      buildParticipants(listingData, contactTypeData, contactSyncData),
    );
    // Re-init only when the underlying deal changes (different deal page) or
    // when reference data loads. NOT on every parent `deal` reference change —
    // that race-condition'd in-flight auto-save clicks against the reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.createdat, contactTypeData?.length, contactSyncData?.length, activeTab]);

  return (
    <div className="bg-white rounded-xl shadow px-4 py-3">
      <div className="flex items-center justify-between mt-5 border-b pb-2">
        <h3 className="text-lg font-semibold">
          {deal?.dealtype === "Buyer" ? "Buy Side Details" : "Listing Details"}
        </h3>

        <div className="flex items-center gap-2">
          {/* Deal stage selector — restored here for buyer deals where
              the standalone Deal Summary header (which used to host it)
              is no longer rendered. Same role gating as before:
              Accountants don't see the editor. */}
          {user.role !== "Accountant" && deal?.dealStage && updatedealstage && (
            <div className="relative">
              <div
                onClick={() => setdealstagepopup(!dealstagepopup)}
                className="hover:cursor-pointer hover:text-white text-black hover:bg-[#1E293B] bg-gray-100 rounded-full px-4 py-1 text-sm"
              >
                <p className="font-medium">{deal.dealStage}</p>
              </div>
              <DealStagePopup
                style={{ top: "36px", right: 0 }}
                visible={dealstagepopup}
                activateoutsidecontainerclick={() =>
                  setdealstagepopup(false)
                }
                onClickcorrect={(item) => {
                  updatedealstage(item);
                  setdealstagepopup(false);
                }}
              />
            </div>
          )}
          <AutoSaveBadge status={autoSaveStatus} lastSavedAt={lastSavedAt} />
        </div>
      </div>

      {/* Auto-save model = the section is always editable; no Edit toggle. */}

      {activeTab === "seller" ? (
        <>
          {/* Deal Summary embedded at the top of Listing Details — same
              treatment as the buyer flow so the two sides look identical
              (just with Pre-sign Date added on the seller side). */}
          <DealSummary
            deal={deal}
            embedded
            editable
            editValues={{
              totalcommissionrate: sellerFormData.totalcommissionrate,
              listingDate: sellerFormData.listingDate,
            }}
            onEditChange={handleSellerFormChange}
          />
          <SellerForm
            isEdit={isEdit}
            electronic={electronicLockboxes}
            pushButton={pushButtonLockboxes}
            contactType={contactTypeData}
            contactSync={contactSyncData}
            formData={sellerFormData}
            onFormChange={handleSellerFormChange}
            onContactSelection={handleContactSelection}
            selectedContacts={selectedContacts}
            participants={participants}
            setParticipants={setParticipants}
            dealId={deal.createdat}
            deal={deal}
            onSubmit={() => handledata()}
          />
        </>
      ) : (
        <>
          {/* Deal Summary is embedded into Buy Side Details so the team
              sees the full deal picture (Important Dates + static fields)
              in one card instead of two. Sale Price / Earnest Money /
              Deal Stage are skipped in embedded mode to avoid duplication
              with Commission Breakdown + the stage pill above. */}
          <DealSummary deal={deal} embedded />
          <BuyerForm
            isEdit={isEdit}
            contactType={contactTypeData}
            contactSync={contactSyncData}
            formData={buyerFormData}
            onFormChange={handleBuyerFormChange}
            onContactSelection={handleContactSelection}
            selectedContacts={selectedContacts}
            participants={participants}
            setParticipants={setParticipants}
            deal={deal}
          />
        </>
      )}

      {/* SellerForm above closes here */}
    </div>
  );
}

// Small status pill showing the current auto-save state. Shown in place of
// the old Edit / Save / Cancel buttons.
function AutoSaveBadge({ status, lastSavedAt }) {
  // "Saved" is the implicit steady state: when nothing has been edited yet
  // (status === "idle" and no save has happened), show nothing.
  if (status === "idle" && !lastSavedAt) return null;

  if (status === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-2 py-1">
        <Loader2 size={12} className="animate-spin" />
        Saving…
      </span>
    );
  }

  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1">
        <AlertCircle size={12} />
        Save failed — will retry on next edit
      </span>
    );
  }

  // saved or idle (with lastSavedAt) — display "Saved · just now / Xs ago".
  const relTime = lastSavedAt ? relativeTime(lastSavedAt) : "";
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1">
      <Check size={12} />
      Saved{relTime ? ` · ${relTime}` : ""}
    </span>
  );
}

function relativeTime(ts) {
  const diff = Math.round((Date.now() - ts) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  return new Date(ts).toLocaleTimeString();
}

export default ListingDetail;
